import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Loader2, MessageCircle, Check } from "lucide-react";
import { Reveal, MaskLines, SectionLabel } from "./Reveal";
import { api } from "../lib/api";
import { useLang } from "../i18n";

const EMPTY = { name: "", contact: "", tour: "moto", date_from: "", date_to: "", people: 2, notes: "" };
const field = "w-full bg-transparent border-b border-[#D4AF37]/30 focus:border-[#D4AF37] outline-none py-3 text-[#F7F4EE] placeholder:text-[#6F6758] transition-colors duration-300";
const label = "block font-mono text-[11px] uppercase tracking-[0.25em] text-[#B5AC9C] mb-1";

export const QuoteForm = () => {
  const { t, lang, wa: waBase } = useLang();
  const q = t.quote;
  const [f, setF] = useState(EMPTY);
  const [state, setState] = useState("idle"); // idle | sending | done | error
  const [emailSent, setEmailSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const summary = () => {
    const tour = q.tours[f.tour];
    const dates = f.date_to ? `${f.date_from} → ${f.date_to}` : f.date_from;
    return `${q.waIntro}\n• ${q.name}: ${f.name}\n• ${q.tour}: ${tour}\n• ${q.dates}: ${dates}\n• ${q.people}: ${f.people}\n• ${q.contact}: ${f.contact}${f.notes ? `\n• ${q.notes}: ${f.notes}` : ""}`;
  };
  const waHref = `${waBase}?text=${encodeURIComponent(summary())}`;

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      const r = await api.post("/quotes", { ...f, people: Number(f.people), lang });
      setEmailSent(!!r.data?.email_sent);
      setState("done");
    } catch {
      setState("error");
    }
  };

  return (
    <section id="quote" className="relative py-16 md:py-40 bg-[#0B0A08] border-t border-[#D4AF37]/15" data-testid="quote-section">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal><SectionLabel index="06">{q.label}</SectionLabel></Reveal>
          <h2 key={lang} className="mt-8 font-display text-4xl sm:text-5xl lg:text-7xl leading-[0.98] text-[#F7F4EE]">
            <MaskLines lines={[q.title]} />
          </h2>
          <Reveal delay={0.1}><p className="mt-8 text-base text-[#CFC6B6] max-w-md leading-relaxed">{q.text}</p></Reveal>
        </div>
        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <div className="relative border border-[#D4AF37]/25 bg-[#14120E]/80 backdrop-blur-xl p-6 sm:p-10 overflow-hidden">
              <div className="absolute inset-0 grain pointer-events-none opacity-60" />
              <AnimatePresence mode="wait">
                {state === "done" ? (
                  <motion.div key="done" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="relative" data-testid="quote-success">
                    <span className="inline-flex w-14 h-14 rounded-full border border-[#D4AF37] text-[#D4AF37] items-center justify-center"><Check /></span>
                    <h3 className="mt-6 font-display text-3xl sm:text-4xl text-[#F7F4EE]">{q.doneTitle}</h3>
                    <p className="mt-4 text-sm sm:text-base text-[#CFC6B6] leading-relaxed max-w-lg">{emailSent ? q.doneText : q.doneTextNoEmail}</p>
                    <a href={waHref} target="_blank" rel="noreferrer" className="btn-gold mt-8 text-sm !px-8 !py-4" data-testid="quote-whatsapp-btn">
                      <MessageCircle size={18} /> {q.waBtn}
                    </a>
                    <button type="button" onClick={() => { setF(EMPTY); setState("idle"); }} className="block mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-[#E6C594] hover:text-[#F3E5AB]" data-testid="quote-reset-btn">{q.another}</button>
                  </motion.div>
                ) : (
                  <motion.form key="form" onSubmit={submit} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="relative grid sm:grid-cols-2 gap-x-8 gap-y-7" data-testid="quote-form">
                    <div>
                      <label className={label} htmlFor="q-name">{q.name}</label>
                      <input id="q-name" required minLength={2} maxLength={80} value={f.name} onChange={set("name")} placeholder={q.namePh} className={field} data-testid="quote-name-input" />
                    </div>
                    <div>
                      <label className={label} htmlFor="q-contact">{q.contact}</label>
                      <input id="q-contact" required minLength={5} maxLength={120} value={f.contact} onChange={set("contact")} placeholder={q.contactPh} className={field} data-testid="quote-contact-input" />
                    </div>
                    <div className="sm:col-span-2">
                      <span className={label}>{q.tour}</span>
                      <div className="mt-2 flex flex-wrap gap-2" role="radiogroup" data-testid="quote-tour-group">
                        {Object.entries(q.tours).map(([k, v]) => (
                          <button type="button" key={k} role="radio" aria-checked={f.tour === k} onClick={() => setF({ ...f, tour: k })} data-testid={`quote-tour-${k}`}
                            className={`px-5 py-2.5 rounded-full border font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${f.tour === k ? "bg-[#D4AF37] border-[#D4AF37] text-[#0B0A08]" : "border-[#D4AF37]/35 text-[#E6C594] hover:border-[#D4AF37]"}`}>
                            {v}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className={label} htmlFor="q-from">{q.dateFrom}</label>
                      <input id="q-from" type="date" required value={f.date_from} onChange={set("date_from")} className={`${field} [color-scheme:dark]`} data-testid="quote-date-from-input" />
                    </div>
                    <div>
                      <label className={label} htmlFor="q-to">{q.dateTo}</label>
                      <input id="q-to" type="date" value={f.date_to} min={f.date_from || undefined} onChange={set("date_to")} className={`${field} [color-scheme:dark]`} data-testid="quote-date-to-input" />
                    </div>
                    <div>
                      <label className={label} htmlFor="q-people">{q.people}</label>
                      <input id="q-people" type="number" required min={1} max={60} value={f.people} onChange={set("people")} className={field} data-testid="quote-people-input" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={label} htmlFor="q-notes">{q.notes}</label>
                      <textarea id="q-notes" rows={3} maxLength={1500} value={f.notes} onChange={set("notes")} placeholder={q.notesPh} className={`${field} resize-none`} data-testid="quote-notes-input" />
                    </div>
                    <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-5 pt-2">
                      <button type="submit" disabled={state === "sending"} className="btn-gold text-sm !px-8 !py-4 disabled:opacity-60" data-testid="quote-submit-btn">
                        {state === "sending" ? <Loader2 size={18} className="animate-spin" /> : <ArrowUpRight size={18} />} {q.submit}
                      </button>
                      <p className="text-xs text-[#8F877A] leading-relaxed max-w-xs">{q.hint}</p>
                    </div>
                    {state === "error" && <p className="sm:col-span-2 text-sm text-red-300" data-testid="quote-error">{q.error}</p>}
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
